/**
 * Company Authentication Context
 * Manages company authentication state and provides context to company components
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../services/firebase/firebase';
import { getCompanyProfile } from '../../features/company/services/companyAuthService';

const CompanyAuthContext = createContext();

/**
 * Hook to use company auth context
 * 
 * @returns {Object} Company auth context value
 */
export const useCompanyAuth = () => {
  const context = useContext(CompanyAuthContext);
  if (!context) {
    throw new Error('useCompanyAuth must be used within a CompanyAuthProvider');
  }
  return context;
};

/**
 * Company Authentication Provider
 * 
 * @param {Object} props - Provider props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Company auth provider
 */
export const CompanyAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setLoading(true);
        setError(null);
        
        if (user) {
          // Get company profile
          const profile = await getCompanyProfile(user.uid);
          
          if (profile && profile.accountType === 'company') {
            setCurrentUser(user);
            setCompanyProfile(profile);
          } else {
            // User is not a company account
            setCurrentUser(null);
            setCompanyProfile(null);
            setError('Access denied. Company account required.');
          }
        } else {
          setCurrentUser(null);
          setCompanyProfile(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setError(error.message);
        setCurrentUser(null);
        setCompanyProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  /**
   * Logout company user
   */
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setCompanyProfile(null);
      setError(null);
    } catch (error) {
      console.error('Error logging out:', error);
      setError(error.message);
    }
  };

  /**
   * Refresh company profile
   */
  const refreshProfile = async () => {
    if (currentUser) {
      try {
        const profile = await getCompanyProfile(currentUser.uid);
        setCompanyProfile(profile);
      } catch (error) {
        console.error('Error refreshing profile:', error);
        setError(error.message);
      }
    }
  };

  const value = {
    currentUser,
    companyProfile,
    loading,
    error,
    logout,
    refreshProfile,
    isAuthenticated: !!currentUser,
    isVerified: companyProfile?.verified || false
  };

  return (
    <CompanyAuthContext.Provider value={value}>
      {children}
    </CompanyAuthContext.Provider>
  );
};

export default CompanyAuthProvider;